import React, { Component } from "react";
import { createPortal } from "react-dom";
import { func, bool, string, oneOf, number } from "prop-types";
import { Transition } from "react-spring/renderprops";
import { Button } from "../button/button.component";

const modalBackdropStyle = {
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
};

const modalBaseStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  overflow: "auto",
  outline: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
};

const modalScrollStyle = {
  alignItems: "normal",
  display: "block",
};

const modalOverlayInnerStyle = {
  height: "calc(100vh - 6.4rem)",
  width: "calc(100vw - 6.4rem)",
};

const modalInnerBaseStyle = {
  backgroundColor: "#fff",
  borderRadius: "0.4rem",
  boxShadow: "0 0.4rem 1.2rem rgba(0, 0, 0, 0.1)",
  position: "relative",
  margin: "1.6rem",
};

const modalInnerSizes = {
  sm: { maxWidth: "30rem", width: "30rem" },
  md: { maxWidth: "42rem", width: "42rem" },
  lg: { maxWidth: "58rem", width: "58rem" },
  xl: { maxWidth: "100rem", width: "100rem" },
  xlvw: { maxWidth: "1600px", width: "96vw" },
  xxl: { maxWidth: "160rem", width: "160rem" },
};

const modalInnerHeights = {
  sm: { height: "30rem" },
  md: { height: "42rem" },
  lg: { height: "58rem" },
  none: {},
};

const modalTitleStyle = {
  fontSize: "1.8rem",
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "1.6rem 1.6rem 1rem 1.6rem",
};

const modalHeadCloseStyle = {
  position: "absolute",
  right: "1rem",
  top: "1rem",
};

const modalBodyStyle = {
  padding: "0 1.6rem 1.6rem 1.6rem",
};

const modalActionsStyle = {
  borderTop: "solid 1px #e6e6e6",
  padding: "1.6rem",
  textAlign: "right",
  display: "flex",
  justifyContent: "flex-end",
  gap: "1rem",
};

const modalStickyStyle = {
  position: "sticky",
  bottom: 0,
  backgroundColor: "#fff",
};

export class Modal extends Component {
  static propTypes = {
    open: bool,
    title: string,
    allowBackdropClick: bool,
    onClose: func,
    size: oneOf(["sm", "md", "lg", "xl", "xlvw", "xxl"]),
    type: oneOf(["dialog", "scroll", "overlay"]),
    closeBtn: bool,
    height: oneOf(["none", "sm", "md", "lg"]),
    level: number,
  };
  static defaultProps = {
    closeBtn: true,
    size: "md",
    type: "dialog",
    height: "none",
    level: 1,
  };

  render() {
    const {
      allowBackdropClick,
      children,
      open,
      title,
      onClose,
      size,
      height,
      closeBtn,
      type,
      level,
    } = this.props;

    // Compose modal styles
    const modalStyle = {
      ...modalBaseStyle,
      ...(type === "scroll" ? modalScrollStyle : {}),
      zIndex: 900 + 100 * level,
    };

    const modalInnerStyle = {
      ...modalInnerBaseStyle,
      ...(modalInnerSizes[size] || {}),
      ...(modalInnerHeights[height] || {}),
      ...(type === "overlay" ? modalOverlayInnerStyle : {}),
    };

    return createPortal(
      <div>
        <>
          <Transition
            items={open}
            from={{ opacity: 0 }}
            enter={{ opacity: 1 }}
            leave={{ opacity: 0 }}
          >
            {(open) =>
              open &&
              ((props) => (
                <div
                  style={{
                    ...modalBackdropStyle,
                    ...props,
                    zIndex: 899 + 100 * level,
                  }}
                  className="modal-backdrop"
                />
              ))
            }
          </Transition>
          <Transition
            items={open}
            from={{ opacity: 0, transform: `translateY(-10rem)` }}
            enter={{ opacity: 1, transform: `translateY(0)` }}
            leave={{ opacity: 0, transform: `translateY(-10rem)` }}
            config={{ mass: 0.5, tension: 120, friction: 10 }}
          >
            {(open) =>
              open &&
              ((props) => (
                <div
                  style={{ ...modalStyle, ...props }}
                  className={`modal${
                    type === "scroll" ? " modal--scroll" : ""
                  }${type === "overlay" ? " modal--overlay" : ""}`}
                  onMouseDown={allowBackdropClick ? onClose : null}
                >
                  <div
                    style={modalInnerStyle}
                    className={`modal__inner modal--${size} height--${height}`}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {closeBtn && (
                      <Button
                        className="modal__head-close"
                        small
                        actionType="flat"
                        icon="fa-regular-times"
                        onClick={onClose}
                        style={modalHeadCloseStyle}
                      />
                    )}
                    {title && (
                      <div className="modal__title" style={modalTitleStyle}>
                        {title}
                      </div>
                    )}
                    <div className="modal__body" style={modalBodyStyle}>
                      {children}
                    </div>
                  </div>
                </div>
              ))
            }
          </Transition>
        </>
      </div>,
      document.body
    );
  }
}
